import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import "./FoldButton.scss";

export default function FoldButton({
  label = "Stage",
  options = [],
  onSelect,
  value,
}) {
  // state
  const [open, setOpen] = useState(false);
  const [internalValue, setInternalValue] = useState(null);

  // refs
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const rectRef = useRef(null);
  const openTimerRef = useRef(null);
  const closeTimerRef = useRef(null);

  // portal position + sizing
  const [portalStyle, setPortalStyle] = useState({
    left: 0,
    top: 0,
    width: "auto",
  });
  const [maxWidth, setMaxWidth] = useState(null);

  // hover timing (you can tweak)
  const HOVER_OPEN_DELAY = 500; // ms — open after 0.5s hover on button
  const HOVER_CLOSE_DELAY = 350; // ms — close after 0.35s when mouse leaves both button and menu

  // ---------- helpers ----------
  const flagKey = (f) =>
    String(f || "")
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, "_")
      .replace(/[^a-z0-9_]/g, "");

  const titleCase = (s = "") =>
    String(s || "")
      .toLowerCase()
      .replace(/_/g, " ")
      .split(" ")
      .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ""))
      .join(" ");

  const STAGE_COLORS = {
    yts: "#93c5fd", // Light Blue - Yet to Start
    progress: "#facc15", // Yellow/Orange - In Progress
    waiting: "#9ca3af", // Gray - Waiting
    on_hold: "#303030", // Black - On Hold
    onhold: "#303030", // Black - On Hold (alt key)
    completed: "#10b981", // Green - Completed
    aborted: "#ef4444", // Red - Aborted
  };

  const colorForFlag = (flag) => {
    const key = flagKey(flag);
    return STAGE_COLORS[key] || "#7f8c8d";
  };

  const uniqueOptions = (() => {
    const seen = new Set();
    return (options || []).filter((o) => {
      const id = String(o.value);
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  })();

  const selectedValue =
    value ?? internalValue ?? (options.length > 0 ? options[0].value : null);

  const selectedOption = options.find(
    (o) => String(o.value) === String(selectedValue),
  );
  const selectedLabelRaw = selectedOption?.label ?? label;
  const selectedLabel = titleCase(selectedLabelRaw);
  const selectedColor = selectedOption
    ? colorForFlag(selectedOption.flag)
    : null;

  // initialize internalValue once when options arrive
  useEffect(() => {
    if (internalValue == null && options.length > 0)
      setInternalValue(options[0].value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.length]);

  // measure max width (preserve your existing logic)
  useEffect(() => {
    if (!options || options.length === 0) return;
    const span = document.createElement("span");
    span.style.visibility = "hidden";
    span.style.position = "absolute";
    span.style.whiteSpace = "nowrap";
    span.style.font = "inherit";
    document.body.appendChild(span);
    let max = 0;
    for (const opt of options) {
      span.textContent = opt.label;
      const w = span.getBoundingClientRect().width;
      if (w > max) max = w;
    }
    document.body.removeChild(span);
    setMaxWidth(max + 30);
  }, [options]);

  useLayoutEffect(() => {
    if (!open) return;
    const btn = buttonRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    rectRef.current = rect;
    const scrollY = window.scrollY || window.pageYOffset;
    const scrollX = window.scrollX || window.pageXOffset;

    // keep menu visually attached to the button (no gap)
    // Using -1px overlap avoids any micro-gap that can cause onMouseLeave -> close race.
    setPortalStyle({
      left: rect.left + scrollX,
      top: rect.bottom + scrollY - 1,
      width: rect.width,
    });
  }, [open]);

  useLayoutEffect(() => {
    if (!options || options.length === 0 || !buttonRef.current) return;

    const btn = buttonRef.current;
    const cs = getComputedStyle(btn);

    const span = document.createElement("span");
    span.style.visibility = "hidden";
    span.style.position = "absolute";
    span.style.whiteSpace = "nowrap";
    span.style.fontFamily = cs.fontFamily;
    span.style.fontSize = cs.fontSize;
    span.style.fontWeight = "700";
    span.style.letterSpacing = cs.letterSpacing;
    document.body.appendChild(span);

    let maxText = 0;
    for (const opt of options) {
      const label = titleCase(opt.label || "");
      span.textContent = label;
      const w = span.getBoundingClientRect().width;
      if (w > maxText) maxText = w;
    }

    const padL = parseFloat(cs.paddingLeft) || 0;
    const padR = parseFloat(cs.paddingRight) || 0;
    const borL = parseFloat(cs.borderLeftWidth) || 0;
    const borR = parseFloat(cs.borderRightWidth) || 0;
    const buffer = 6;

    const total = Math.ceil(maxText + padL + padR + borL + borR + buffer);
    setMaxWidth(total);

    document.body.removeChild(span);
  }, [options]);

  // ---------- timer helpers ----------
  const clearOpenTimer = () => {
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
  };
  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const startOpenTimer = () => {
    // if already open, nothing to do
    if (open) return;
    clearCloseTimer(); // cancel a pending close
    if (openTimerRef.current) return;
    openTimerRef.current = setTimeout(() => {
      openTimerRef.current = null;
      setOpen(true);
      // when it opens, cancel any stray close timers
      clearCloseTimer();
    }, HOVER_OPEN_DELAY);
  };

  const startCloseTimer = () => {
    // if already closed, nothing to do
    if (!open) {
      clearOpenTimer();
      return;
    }
    clearOpenTimer();
    if (closeTimerRef.current) return;
    closeTimerRef.current = setTimeout(() => {
      closeTimerRef.current = null;
      setOpen(false);
      // also clear any stray open timer
      clearOpenTimer();
    }, HOVER_CLOSE_DELAY);
  };

  // cleanup timers on unmount
  useEffect(() => {
    return () => {
      clearOpenTimer();
      clearCloseTimer();
    };
  }, []);

  // close on outside click + esc (keeps working)
  useEffect(() => {
    if (!open) return;
    function onDocClick(e) {
      if (
        buttonRef.current?.contains(e.target) ||
        menuRef.current?.contains(e.target)
      )
        return;
      setOpen(false);
    }
    function onEsc(e) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  // selection handler — clear timers first so selection can't race with close
  const handleSelect = (v, e) => {
    if (e?.preventDefault) e.preventDefault();
    if (e?.stopPropagation) e.stopPropagation();

    clearOpenTimer();
    clearCloseTimer();

    if (value === undefined) setInternalValue(v);

    try {
      onSelect?.(v);
    } catch (err) {
      console.error("FoldButton onSelect threw:", err);
    }

    // close menu immediately after selection
    setOpen(false);
  };

  // menu element (portal)
  const menuEl = (
    <ul
      ref={menuRef}
      className={`dropdown-portal-menu ${open ? "visible" : ""}`}
      style={{
        position: "absolute",
        left: portalStyle.left + "px",
        top: portalStyle.top + "px",
        width: portalStyle.width + "px",
        zIndex: 9999,
        pointerEvents: "auto",
      }}
      // IMPORTANT: keep the menu open while the pointer is over it
      onMouseEnter={() => {
        // user hovering menu: cancel any close timer
        clearCloseTimer();
      }}
      onMouseMove={() => {
        // defensive: if pointer moves inside menu quickly, ensure close timer cleared
        clearCloseTimer();
      }}
      onMouseLeave={() => {
        // start close timer when pointer leaves menu
        startCloseTimer();
      }}
      role="menu"
    >
      {uniqueOptions.map((opt) => {
        const color = colorForFlag(opt.flag);
        return (
          <li
            key={opt.value}
            className="dropdown-item"
            style={{
              backgroundColor: color + "20",
              color,
              borderLeft: `4px solid ${color}`,
              cursor: "pointer",
            }}
            // onMouseDown ensures selection happens before possible mouseleave/close
            onMouseDown={(e) => handleSelect(opt.value, e)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleSelect(opt.value, e);
              }
            }}
            role="menuitem"
            tabIndex={0}
          >
            {titleCase(opt.label)}
          </li>
        );
      })}
    </ul>
  );

  // render
  return (
    <>
      <button
        ref={buttonRef}
        className={`fold-button ${open ? "open" : ""}`}
        type="button"
        onClick={() => {
          // immediate toggle on click; cancel timers so they don't race
          clearOpenTimer();
          clearCloseTimer();
          setOpen((s) => !s);
        }}
        onMouseEnter={() => {
          // start delayed open when hovering button
          startOpenTimer();
        }}
        onMouseLeave={() => {
          // when leaving button, start close timer (menu might still cancel it)
          startCloseTimer();
        }}
        style={{
          backgroundColor: selectedColor || "#3b82f6",
          "--grad-light": selectedColor
            ? `${selectedColor}33`
            : "rgba(0, 0, 0, 1)",
          "--grad-dark": selectedColor || "rgba(255, 255, 255, 1)",
          width: maxWidth ? `${maxWidth}px` : "auto",
          boxSizing: "border-box",
        }}
      >
        <b>{selectedLabel}</b>
      </button>

      {open && createPortal(menuEl, document.body)}
    </>
  );
}
